import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { ChevronRight, Lock, Grid3x3, Shuffle, Puzzle, Key, Eye, HelpCircle, MapPin, Users, GitBranch, Lightbulb, BookOpen } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import PuzzleMotif from '@/components/PuzzleMotif';
import { getLanguageContent } from '@/data/languageContent';
import useSEO from '@/hooks/useSEO';

const PUZZLE_TYPES = [
    { slug: 'word_search',    name: 'Word Search',   icon: Grid3x3,    description: 'Find hidden words in the grid.', free: true,  requiresPhrases: false },
    { slug: 'balloon_pop',    name: 'Balloon Pop',   icon: HelpCircle, description: 'Guess letters, save the balloons.', free: false, requiresPhrases: false },
    { slug: 'word_scramble',  name: 'Word Scramble', icon: Shuffle,    description: 'Unscramble the letters.', free: false, requiresPhrases: false },
    { slug: 'crossword',      name: 'Crossword',     icon: Puzzle,     description: 'English clues, foreign answers.', free: false, requiresPhrases: false },
    { slug: 'missing_vowel',  name: 'Missing Vowel', icon: Eye,        description: 'Fill in the vowels that vanished.', free: false, requiresPhrases: false },
    { slug: 'cryptogram',     name: 'Cryptogram',    icon: Key,        description: 'Crack the code, read the sentence.', free: false, requiresPhrases: true },
];

export default function LanguageHub() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [hub, setHub] = useState(null);
    const [err, setErr] = useState(null);
    const content = getLanguageContent(slug);

    useEffect(() => {
        api.get(`/languages/${slug}`).then(({ data }) => setHub(data)).catch((e) => setErr(e?.response?.data?.detail || 'Failed to load'));
    }, [slug]);

    // SEO — runs on every render once hub/content are known; safe to call before data arrives.
    const seoTitle = hub
        ? `Learn ${hub.language.name} — puzzles, phrases & pronunciation | Linguistic Quest`
        : `Learn ${slug.charAt(0).toUpperCase() + slug.slice(1)} | Linguistic Quest`;
    const seoDesc = content
        ? `${content.tagline} Learn ${hub?.language?.name || slug} at home with word search, crossword and pronunciation puzzles for children. ${content.overview[0].slice(0, 110)}…`
        : `Learn ${slug} with family-friendly puzzles: word search, crossword, balloon pop, word scramble and more. Native-style pronunciation for every word.`;
    useSEO({
        title: seoTitle,
        description: seoDesc,
        path: `/languages/${slug}`,
        image: '/brand/og-default.jpg',
        jsonLdId: `language-${slug}`,
        jsonLd: hub && content ? {
            '@context': 'https://schema.org',
            '@type': 'Course',
            name: `${hub.language.name} for children`,
            description: content.overview[0],
            provider: {
                '@type': 'Organization',
                name: 'Linguistic Quest',
                url: 'https://www.linguisticquest.com/',
            },
            inLanguage: hub.language.name,
            educationalLevel: 'children',
            audience: { '@type': 'EducationalAudience', educationalRole: 'student' },
        } : null,
    });

    if (err) return <main className="max-w-4xl mx-auto px-6 py-16">Error: {err}</main>;
    if (!hub) return <main className="max-w-4xl mx-auto px-6 py-16 text-lq-ink-500">Loading {slug}…</main>;

    const lang = hub.language;
    const primaryTier = hub.age_tiers[0]?.slug || 'seedlings';
    const hasPhrases = hub.phrase_count > 0;

    return (
        <main className="max-w-7xl mx-auto px-6 py-14 relative" data-testid="page-language-hub">
            <div className="absolute top-0 right-0 -z-10 pointer-events-none"><PuzzleMotif size={220} opacity={0.08}/></div>
            <nav className="text-sm text-lq-ink-500 flex items-center gap-1" aria-label="Breadcrumb">
                <Link to="/languages" className="hover:text-lq-purple-700">Languages</Link>
                <ChevronRight size={14} /> <span className="text-lq-ink-900">{lang.name}</span>
            </nav>
            <div className="mt-4 flex flex-wrap items-center gap-4">
                <span className="text-6xl" aria-hidden="true">{lang.primary_flag_emoji}</span>
                {lang.display_flag_mode === 'dual' && lang.secondary_flag_emoji && (
                    <span className="text-4xl opacity-80" aria-hidden="true">{lang.secondary_flag_emoji}</span>
                )}
                <div>
                    <h1 className="font-display text-4xl md:text-5xl font-semibold text-lq-ink-black">{lang.name}</h1>
                    {content?.tagline && <p className="mt-2 text-lg text-lq-ink-700 max-w-2xl">{content.tagline}</p>}
                    <p className="mt-1 text-sm text-lq-ink-500">{hub.themes.length} themes · {hub.age_tiers.length} age tiers · puzzle library keeps growing</p>
                </div>
            </div>

            {/* Rich editorial content — substantive, unique, indexable. */}
            {content && (
                <section className="mt-10 grid md:grid-cols-4 gap-4" data-testid="language-facts" aria-label={`Key facts about ${lang.name}`}>
                    <Fact icon={Users}    label="Speakers" value={content.speakers} />
                    <Fact icon={MapPin}   label="Where it's spoken" value={content.regions} />
                    <Fact icon={GitBranch} label="Language family" value={content.family} />
                    <Fact icon={BookOpen} label="In the app"
                          value={`${hub.themes.length} themes, ${PUZZLE_TYPES.length} puzzle types, ${hub.age_tiers.length} age tiers`} />
                </section>
            )}

            {content && (
                <section className="mt-12 max-w-3xl" data-testid="language-overview">
                    <h2 className="font-display text-2xl font-semibold text-lq-ink-900">About {lang.name}</h2>
                    {content.overview.map((p, i) => (
                        <p key={i} className="mt-4 text-lq-ink-700 leading-relaxed">{p}</p>
                    ))}
                    {content.cultureNote && (
                        <blockquote className="mt-6 border-l-4 border-lq-purple-300 pl-4 italic text-lq-ink-700">
                            {content.cultureNote}
                        </blockquote>
                    )}
                </section>
            )}

            {content?.sampleWords?.length > 0 && (
                <section className="mt-12" data-testid="language-samples">
                    <h2 className="font-display text-2xl font-semibold text-lq-ink-900">A handful of {lang.name} to take away</h2>
                    <p className="mt-2 text-sm text-lq-ink-500">These appear across our puzzles — every word in the app is paired with a translation and a spoken voice.</p>
                    <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {content.sampleWords.map((w, i) => (
                            <div key={i} className="bg-white border border-lq-ink-100 rounded-xl px-4 py-3" data-testid={`sample-word-${i}`}>
                                <div className="font-display text-lg font-semibold text-lq-ink-900">{w.native}</div>
                                <div className="text-sm text-lq-ink-500">{w.en}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {content?.learnTips?.length > 0 && (
                <section className="mt-12 max-w-3xl bg-lq-purple-50 border border-lq-purple-100 rounded-2xl p-6" data-testid="learn-tips">
                    <div className="flex items-center gap-2 text-lq-purple-700 font-display text-xl font-semibold">
                        <Lightbulb size={20} /> Tips from our editors
                    </div>
                    <ul className="mt-3 space-y-2 text-lq-ink-700">
                        {content.learnTips.map((t, i) => (
                            <li key={i} className="flex gap-2"><span className="text-lq-purple-500">•</span>{t}</li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Puzzle picker */}
            <h2 className="mt-16 font-display text-2xl font-semibold text-lq-ink-900">Pick a theme to play</h2>
            <p className="mt-2 text-sm text-lq-ink-500 max-w-2xl">Each theme gathers 40–80 words, phrases and idioms. Every puzzle type teaches the same vocabulary in a different way — children absorb the words faster because they meet them again and again from new angles.</p>
            <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="themes-grid">
                {hub.themes.map((t) => (
                    <div key={t.id} className="group relative bg-white border border-lq-ink-100 rounded-2xl p-5 hover:border-lq-purple-300 hover:shadow transition-all" data-testid={`theme-${t.slug}`}>
                        <div className="absolute top-4 right-4 text-lq-purple-300 opacity-50 group-hover:opacity-100 transition-opacity">
                            <Puzzle size={18} />
                        </div>
                        <h3 className="font-display text-lg font-semibold text-lq-ink-900">{t.name}</h3>
                        <p className="mt-1 text-xs text-lq-ink-500">growing library</p>
                        <div className="mt-4 space-y-1.5">
                            {PUZZLE_TYPES.map((p) => {
                                const unavailable = p.requiresPhrases && !hasPhrases;
                                const free = lang.free_tier_unlock && p.free;
                                const canPlay = !unavailable && (free || !!user);
                                const href = `/play/${lang.slug}/${t.slug}/${primaryTier}/${p.slug}`;
                                return (
                                    <Link
                                        key={p.slug}
                                        to={canPlay ? href : (unavailable ? '#' : '/pricing')}
                                        className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                            canPlay
                                                ? 'bg-lq-purple-50 text-lq-purple-900 hover:bg-lq-purple-100'
                                                : 'bg-lq-ink-100 text-lq-ink-500 cursor-not-allowed'
                                        }`}
                                        onClick={unavailable ? (e) => e.preventDefault() : undefined}
                                        data-testid={`play-${t.slug}-${p.slug}`}
                                        title={unavailable ? 'Phrases coming soon for this theme' : undefined}
                                    >
                                        <span className="flex items-center gap-2"><p.icon size={14} /> {p.name}</span>
                                        {canPlay ? <ChevronRight size={14} /> : <Lock size={12} />}
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 text-center text-sm text-lq-ink-500" data-testid="more-coming-note">
                More themes, more puzzle types, and more languages land regularly — your subscription covers everything we add.
            </div>

            {!user && (
                <div className="mt-8 p-5 rounded-xl bg-lq-peach-100 border border-lq-peach-300 flex flex-wrap gap-3 items-center justify-between" data-testid="paywall-banner">
                    <p className="text-sm text-lq-ink-900 font-medium">
                        Word Search is free in Shona. Sign up to unlock the other puzzle types and every other language.
                    </p>
                    <Link to="/register" className="px-5 py-2 rounded-full bg-lq-purple-500 text-white font-semibold hover:bg-lq-purple-700" data-testid="hub-cta-register">
                        Start free
                    </Link>
                </div>
            )}

            {/* Internal linking block — helps crawlers and users explore adjacent languages. */}
            <section className="mt-16 border-t border-lq-ink-100 pt-8" data-testid="related-languages">
                <h2 className="font-display text-xl font-semibold text-lq-ink-900">Explore other languages on Linguistic Quest</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                    {['shona','ndebele','yoruba','swahili','french','portuguese','zulu','xhosa','amharic','hausa','igbo','chichewa','afrikaans','spanish']
                        .filter(s => s !== slug)
                        .map(s => (
                            <Link key={s} to={`/languages/${s}`} className="px-3 py-1.5 rounded-full bg-white border border-lq-ink-100 text-sm text-lq-ink-700 hover:border-lq-purple-300 hover:text-lq-purple-700 transition-colors">
                                {s.charAt(0).toUpperCase() + s.slice(1)}
                            </Link>
                        ))}
                </div>
            </section>
        </main>
    );
}

function Fact({ icon: Icon, label, value }) {
    return (
        <div className="bg-white border border-lq-ink-100 rounded-xl p-4">
            <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-lq-purple-100 text-lq-purple-700 mb-2">
                <Icon size={16} />
            </div>
            <div className="text-xs uppercase tracking-wide text-lq-ink-500">{label}</div>
            <div className="mt-1 text-sm text-lq-ink-900 leading-snug">{value}</div>
        </div>
    );
}
